import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Loader2, AlertCircle, XCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [cartCleared, setCartCleared] = useState(false);
  const { clearCart } = useCart();
  const maxPollAttempts = 10;
  const pollInterval = 2000; // 2 seconds

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    if (attempts >= maxPollAttempts) {
      // Max attempts reached - show pending message
      setStatus('pending');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      
      setPaymentStatus(data);
      setPollAttempts(attempts + 1);

      if (data.payment_status === 'paid') {
        setStatus('success');
        
        // Clear cart only once after successful payment
        if (!cartCleared) {
          await clearCart();
          setCartCleared(true);
          // Clear session storage for pending order
          sessionStorage.removeItem('pending_order_id');
          sessionStorage.removeItem('pending_session_id');
        }
        
        // Fetch order details
        try {
          const orderResponse = await axios.get(`${API}/order-by-session/${sessionId}`);
          setOrderInfo(orderResponse.data);
        } catch (err) {
          // Order info fetch failed, but payment succeeded - that's ok
          console.log('Could not fetch order details');
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Retry on error
      if (attempts < maxPollAttempts) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, cartCleared, clearCart]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setStatus('error');
    }
  }, [sessionId, pollPaymentStatus]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="loading-spinner">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
          <p className="text-sm text-gray-400 mt-2">Attempt {pollAttempts + 1} of {maxPollAttempts}</p>
        </div>
      </div>
    );
  }

  // Payment still processing
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-white" data-testid="order-pending-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h1
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Payment Processing
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your payment is still being processed. This may take a few moments.
              You will receive an email confirmation once your order is confirmed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                className="bg-[#D4AF37] text-white py-3 px-8 rounded-full hover:bg-[#B5952F] transition-colors inline-block"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Session expired
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-white" data-testid="order-expired-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Session Expired
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cart"
                className="bg-[#D4AF37] text-white py-3 px-8 rounded-full hover:bg-[#B5952F] transition-colors inline-block"
              >
                Return to Cart
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white" data-testid="order-error-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Something Went Wrong
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              We couldn't verify your payment status. If you completed the payment,
              please check your email for confirmation or contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                className="bg-[#D4AF37] text-white py-3 px-8 rounded-full hover:bg-[#B5952F] transition-colors inline-block"
              >
                Continue Shopping
              </Link>
              <a
                href="mailto:info@neporaforyou.com"
                className="bg-white text-black border border-black py-3 px-8 rounded-full hover:bg-gray-50 transition-colors inline-block"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-white" data-testid="order-success-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
          <h1
            className="text-4xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="success-title"
          >
            Order Confirmed!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Thank you for your purchase! Your payment was successful and your order is being processed.
          </p>

          {orderInfo && (
            <div className="bg-[#F9F9F9] p-6 rounded-lg mb-8 text-left">
              <div className="flex items-center mb-4">
                <Package className="w-5 h-5 mr-2 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold">Order Details</h2>
              </div>
              <div className="space-y-2 text-sm">
                {orderInfo.order_number && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Number</span>
                    <span className="font-medium font-mono">{orderInfo.order_number}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status</span>
                  <span className="font-medium text-green-600 capitalize">Paid</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Status</span>
                  <span className="font-medium text-blue-600 capitalize">Processing</span>
                </div>
                {paymentStatus?.amount_total && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Paid</span>
                    <span className="font-medium">
                      ${(paymentStatus.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {!orderInfo && paymentStatus && (
            <div className="bg-[#F9F9F9] p-6 rounded-lg mb-8 text-left">
              <div className="flex items-center mb-4">
                <Package className="w-5 h-5 mr-2 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold">Payment Details</h2>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status</span>
                  <span className="font-medium text-green-600 capitalize">Paid</span>
                </div>
                {paymentStatus.amount_total && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-medium">
                      ${(paymentStatus.amount_total / 100).toFixed(2)} {paymentStatus.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <p className="text-gray-600">
              A confirmation email has been sent to your email address with order details.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                className="bg-[#D4AF37] text-white py-3 px-8 rounded-full hover:bg-[#B5952F] transition-colors inline-block"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </Link>
              <Link
                to="/"
                className="bg-white text-black border border-black py-3 px-8 rounded-full hover:bg-gray-50 transition-colors inline-block"
                data-testid="home-button"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
